import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La password deve avere almeno 8 caratteri");
      return;
    }
    if (password !== confirm) {
      setError("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md animate-fade-up">
        <img src="/studio-castelli-logo.jpg" alt="Studio Castelli" className="w-44 h-auto mb-10" />

        <h2 className="font-display text-3xl font-bold text-navy tracking-tight mb-2">Nuova password</h2>
        <p className="text-muted-foreground text-sm mb-8">Scegli una nuova password di almeno 8 caratteri.</p>

        {!token && (
          <div data-testid="reset-password-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-5">
            Link non valido: richiedi un nuovo link di reimpostazione.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
          <div className="space-y-2">
            <Label htmlFor="password">Nuova password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-11"
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Conferma password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="pl-10 h-11"
                data-testid="reset-password-confirm-input"
              />
            </div>
          </div>
          {error && (
            <div data-testid="reset-password-error-message" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading || !token}
            data-testid="reset-password-submit-button"
            className="w-full h-11 bg-navy hover:bg-navy-light text-white font-semibold transition-colors duration-200"
          >
            {loading ? "Salvataggio..." : "Reimposta password"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="inline-flex items-center gap-2 text-sm text-navy/70 hover:text-gold transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
